import { useState } from "react";
import CustomerAddEdit from "./CustomerAddEdit";
import Button from "../../ui/Button";
import { AddIcon, DeleteIcon, ViewIcon } from "../../utils/icon.utils";
import Table from "../../ui/Table/Table";
import { GroupBtn } from "../../ui/GroupBtn";
import {
  getGenderByValue,
  useDeleteCustomerMutation,
  useGetAllCustomerQuery
} from "../../utils/customer.utils";
import { tableIndex } from "../../utils/table.utils";
import { onConfirm } from "../../utils/sweetAlert.utils";
import Loading from "../../components/Loading";
import PageLayout from "../../layouts/PageLayout";
import CustomerFilter from "./CustomerFilter";
import { multiFullAddressView } from "../../utils/address.utils";
import { useNavigate } from "react-router-dom";
import { arrayMap } from "../../utils/array.utils";
import SearchList from "./SearchList";
import { cleanDuplicateQuery, useQueryParams } from "../../utils/queryParams.utils";

export default function CustomerPage() {
  const navigate = useNavigate();
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [filter, setFilter] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [params, setParams] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [activeFilterId, setActiveFilterId] = useState("");
  const [modal, setModal] = useState(false);
  const { searchParams } = useQueryParams();

  const toggle = () => {
    if (modal) {
      setEditItem(null);
    }
    setModal((prev) => !prev);
  };

  const { loading, data } = useGetAllCustomerQuery({
    query: cleanDuplicateQuery(
      `?page=${currentPage}&size=${pageSize}&type=${activeFilterId}&search=${searchText}&${searchParams}`
    )
  });

  const { loading: deleting, deleteCustomer } = useDeleteCustomerMutation(["deleteCustomer"]);

  const totalPage = data?.last_page === 1 && data?.total === 0 ? 0 : data?.last_page;

  const breadCrumbs = [
    {
      name: "Pages",
      link: "/pages"
    },
    {
      name: "Customer"
    }
  ];

  const thead = ["#", "CID", "Name", "Gender", "Mobile", "Email", "Address", "Action"];

  const tbody =
    arrayMap(data?.data, (item, index) => {
      const address =
        multiFullAddressView(item?.addresses) !== "" ? multiFullAddressView(item?.addresses) : "-";
      return {
        "#": tableIndex(index, currentPage, pageSize),
        CID: item?.customer_id ?? "-",
        Name: item?.name ?? "-",
        Gender: getGenderByValue(item?.gender) ?? "-",
        Mobile: item?.mobile ?? "-",
        Email: item?.email ?? "-",
        Address: address,
        ...item
      };
    }) || [];

  const ActionComponent = ({ data }) => {
    const handleView = (item) => {
      navigate(`/customer/${item?.customer_id}/view`);
    };

    const handleDelete = async (item) => {
      const { isConfirmed } = await onConfirm();
      if (isConfirmed) {
        await deleteCustomer(item?.id);
      }
    };

    const btns = [
      {
        icon: DeleteIcon,
        onClick: () => handleDelete(data),
        color: "danger"
      },
      {
        icon: ViewIcon,
        onClick: () => handleView(data),
        color: "primary"
      }
    ];
    return <GroupBtn btns={btns} justify="start" />;
  };

  const isLoading = loading || deleting;

  const handleFilter = (filterId) => {
    setActiveFilterId(filterId);
  };

  return (
    <PageLayout title="Customer" subTitle={`Total: ${data?.total ?? 0}`} breadCrumbs={breadCrumbs}>
      <div className="row mb-3">
        <div className="col-md-6 col-lg-4 mb-2 mb-md-0">
          <SearchList
            setSearchText={setSearchText}
            searchText={searchText}
            setFilter={setFilter}
            setParams={setParams}
          />
        </div>

        <div className="col-md-6 col-lg-8">
          <div className="d-flex align-items-center justify-content-end gap-1">
            <Button color="primary" icon={AddIcon} name="Create Customer" onClick={toggle} />
            <CustomerFilter activeId={activeFilterId} handleClick={handleFilter} />
          </div>
        </div>
      </div>

      {loading ? "Loading..." : ""}

      <Table
        thead={thead}
        tbody={tbody}
        action={ActionComponent}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPage={totalPage}
      />

      {modal && <CustomerAddEdit modal={modal} toggle={toggle} editItem={editItem} />}
      {<Loading loading={isLoading} />}
    </PageLayout>
  );
}
